import { NeighborhoodServiceAreaInfo } from 'rwl-shared/dist/models';
import React from 'react';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { Body2, ButtonBase, Subtitle1 } from 'rwl-ui';
import { When } from 'react-if';
import { useGlobal } from '../../../hooks';
import { getIcon } from '../../../utils/mapUtils';

const Container = styled.div`
    min-width: 197px;
    height: 100%;
    border-radius: var(--rwl-radius-sm);
    display: inline-flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    cursor: pointer;
`;

const Content = styled.div`
    width: 100%;
    padding: 8px 12px 12px 12px;
    background: var(--rwl-palette-white);
    box-shadow: var(--rwl-shadow-md);
    border-radius: var(--rwl-radius-sm);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 0;
`;

const TopSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--rwl-spacing-xs);
`;

const TitleSection = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: var(--rwl-spacing-xs);
`;

const ZipCode = styled(Subtitle1)`
    line-height: 28px;
    font-weight: 500;
`;

const City = styled(Body2)`
    position: relative;
    margin-bottom: 0px;
    height: 100%;
    background-color: var(--rwl-palette-white);
    margin-top: -4px;
    font-weight: 500;
`;

const StatsRow = styled.div`
    display: flex;
    align-items: center;
    gap: var(--rwl-spacing-md);
    padding: 0;
    margin-top: var(--rwl-spacing-xs);
`;

const StatItem = styled.div`
    display: flex;
    align-items: center;
    gap: var(--rwl-spacing-xs);
`;

const StatTextGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 3px;
`;

const StatText = styled(Body2)`
    color: var(--rwl-text-default);
`;

const ButtonContainer = styled.div`
    width: 100%;
    display: flex;
    margin-top: 12px;
`;

type SingleZipPopoverProps = {
    serviceArea: NeighborhoodServiceAreaInfo;
    onClick?: () => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
};

const SingleZipPopover: React.FC<SingleZipPopoverProps> = ({
    serviceArea: {
        postcode,
        neighborhood,
        jobCount,
        reviewCount,
        averageRating,
    },
    onClick,
    onMouseEnter,
    onMouseLeave,
}) => {
    const { config } = useGlobal();
    const icon = config?.account.mapSettings.icon;
    const iconTint = config?.account.mapSettings.color;

    return (
        <Container
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={onClick}
        >
            <Content>
                <TopSection>
                    <TitleSection>
                        <ZipCode>{postcode}</ZipCode>
                        <City>{neighborhood}</City>
                    </TitleSection>
                    <StatsRow>
                        <StatItem>
                            <FontAwesomeIcon
                                icon={getIcon(icon)}
                                color={iconTint}
                                style={{ width: 14, height: 14 }}
                            />
                            <StatTextGroup>
                                <StatText>{jobCount}</StatText>
                                <StatText>projects</StatText>
                            </StatTextGroup>
                        </StatItem>
                        <When condition={reviewCount > 0}>
                            <StatItem>
                                <FontAwesomeIcon
                                    icon={faStar}
                                    color="var(--rwl-palette-amber-400)"
                                    style={{ width: 14, height: 14 }}
                                />
                                <StatText>
                                    {averageRating?.toFixed(1)} ({reviewCount})
                                </StatText>
                            </StatItem>
                        </When>
                    </StatsRow>
                </TopSection>
                <ButtonContainer>
                    <ButtonBase
                        className={'SeeProjectsButton'}
                        onClick={onClick}
                        style={{
                            borderRadius: 'var(--rwl-radius-lg)',
                            backgroundColor: '#00000014',
                            color: 'var(--rwl-text-default)',
                            width: '100%',
                            height: '28px',
                            fontSize: '12px',
                            fontWeight: '500',
                            lineHeight: '20px',
                            wordWrap: 'break-word',
                        }}
                    >
                        See projects
                    </ButtonBase>
                </ButtonContainer>
            </Content>
        </Container>
    );
};

export default SingleZipPopover;
